/**
 * TEMP-H-001: useStageThresholds Hook
 * 
 * React Query hook for fetching stage-specific thresholds and persona settings.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { executionAPI } from '@/lib/execution-api';
import { executionKeys, type StageThresholds, APIClientError } from '@/lib/api-types';
import type { StageType } from '@/types/execution';

interface UseStageThresholdsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useStageThresholds(
  executionId: string,
  stage: StageType,
  options: UseStageThresholdsOptions = {}
): UseQueryResult<StageThresholds, APIClientError> {
  const { enabled = true, refetchInterval } = options;

  return useQuery({
    queryKey: executionKeys.stageThresholds(executionId, stage),
    queryFn: () => executionAPI.getStageThresholds(executionId, stage),
    enabled: enabled && !!executionId && !!stage,
    refetchInterval,
    staleTime: 60 * 1000, // 1 minute - thresholds don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (execution/stage not found) or 403 (unauthorized)
      if (error.status === 404 || error.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export default useStageThresholds;